import React, {useMemo} from 'react';
import styled, {useTheme} from 'styled-components';
import {Form} from 'react-bootstrap';
import Select, {FormatOptionLabelMeta, StylesConfig} from 'react-select';
import {MIXED} from 'components/customer/Product/Drawer/helpers';
import {tint} from 'theme/helper';
import {COMPACT_PAD} from 'components/customer/Product/Specs/DrawerFieldsRow';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';

export interface Option {
    label: string;
    value: string;
    disabled?: boolean;
    // why it is disabled, under it in the list
    reason?: string;
}

interface HardwareDefaultsRowProps {
    typeOptions: Option[];
    // the value every drawer shares, or MIXED
    type: string;
    onTypeChange: (option: Option) => void;
    runnerOptions: Option[];
    runner: string;
    onRunnerChange: (option: Option) => void;
    onRunnerMenuOpen?: () => void;
    runnerDisabled?: boolean;
    // under the runner, as why it is disabled
    runnerNote?: string;
    // off when the row sits straight under the card header, which has its own
    divided?: boolean;
}

// shown while the drawers disagree, until the customer picks a real value
const mixedOption = (value: string): Option =>
    value === MIXED ? {label: MIXED, value: MIXED} : null;

const isOptionDisabled = (option: Option) => !!option?.disabled;

const selected = (options: Option[], value: string) =>
    options.find((option) => option.value === value) ?? mixedOption(value);

const formatOption = (
    option: Option,
    {context}: FormatOptionLabelMeta<Option>
) =>
    context === 'menu' && option.reason ? (
        <>
            {option.label}
            <Reason>{option.reason}</Reason>
        </>
    ) : (
        option.label
    );

// The Default Type and Default Runner pair, for the cabinet and inner drawers.
export const HardwareDefaultsRow = ({
    typeOptions,
    type,
    onTypeChange,
    runnerOptions,
    runner,
    onRunnerChange,
    onRunnerMenuOpen,
    runnerDisabled = false,
    runnerNote,
    divided = true,
}: HardwareDefaultsRowProps) => {
    const compact = useCompactLayout();

    // matches SelectField, so these read the same as the per drawer controls
    const theme = useTheme();
    const selectStyles = useMemo<StylesConfig<Option, false>>(
        () => ({
            control: (base) => ({
                ...base,
                backgroundColor: 'white',
                borderColor: theme.colors.primary?.main,
                borderWidth: '2px',
                borderRadius: '8px',
                boxShadow: 'none',
                ':hover': {borderColor: theme.colors.primary?.main},
            }),
            dropdownIndicator: (base) => ({...base, padding: '7px'}),
            menu: (base) => ({...base, zIndex: 5}),
            option: (base, {isDisabled}) => ({
                ...base,
                overflowWrap: 'break-word',
                // no highlight, which would read as pickable
                ...(isDisabled
                    ? {
                          color: '#8a929b',
                          cursor: 'not-allowed',
                          backgroundColor: 'transparent',
                      }
                    : {}),
            }),
        }),
        [theme]
    );

    return (
        <Row
            data-cy="drawer-hardware-defaults"
            $compact={compact}
            $divided={divided}>
            <Group>
                <Form.Label htmlFor="default-drawer-type">
                    Default Type
                    <span>*</span>
                </Form.Label>
                <Select
                    inputId="default-drawer-type"
                    styles={selectStyles}
                    options={typeOptions}
                    isOptionDisabled={isOptionDisabled}
                    value={selected(typeOptions, type)}
                    onChange={onTypeChange}
                />
            </Group>

            <Group>
                <Form.Label htmlFor="default-drawer-runner">
                    Default Runner
                    <span>*</span>
                </Form.Label>
                <Select
                    inputId="default-drawer-runner"
                    styles={selectStyles}
                    options={runnerOptions}
                    isOptionDisabled={isOptionDisabled}
                    isDisabled={runnerDisabled}
                    formatOptionLabel={formatOption}
                    value={selected(runnerOptions, runner)}
                    onChange={onRunnerChange}
                    onMenuOpen={onRunnerMenuOpen}
                />
                {runnerNote ? (
                    <Note data-cy="drawer-runner-note">{runnerNote}</Note>
                ) : null}
            </Group>
        </Row>
    );
};

const Row = styled.div<{$compact: boolean; $divided: boolean}>`
    display: flex;
    gap: ${({$compact}) => ($compact ? 10 : 12)}px;
    /* the gutter the collapsible rows and the drawer settings use */
    padding: 11px ${({$compact}) => ($compact ? COMPACT_PAD : 18)}px;
    ${({$divided, theme}) =>
        $divided
            ? `border-top: 1px solid ${tint(
                  String(theme.colors.primary?.main),
                  0.12
              )};`
            : ''}

    /* the card is narrow in the room planner while the window is not */
    ${({$compact}) => ($compact ? 'flex-wrap: wrap;' : '')}
`;

// a label above its control, in the metrics the per drawer controls use
export const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;

    /* the same label metrics the per drawer controls use */
    label {
        font-size: 13.6px;
        font-weight: 700;
        padding: 7px 0;
        margin-bottom: 0;
        color: ${({theme}) => theme.colors.primary?.main};

        /* the app reddens a required marker through .cbc-form .form-group,
           which this card sits outside of */
        span {
            color: ${({theme}) => theme.colors.errorLabel?.main};
        }
    }
`;

const Group = styled(FieldGroup)`
    flex: 1 1 0;
    min-width: 0;
`;

const Reason = styled.span`
    display: block;
    font-size: 12px;
`;

const Note = styled.span`
    margin-top: 4px;
    font-size: 12px;
    color: #6b7480;
`;
